<template>
  <div class="lb-wrap">
    <!-- Fixed header: title/tabs + column headings stay put while only the
         rows below scroll. -->
    <div class="lb-header">
      <v-row no-gutters class="lb-bar">
        <v-col cols="auto">
          <!-- Any scoped view shows a breadcrumb rooted at a clickable mode
               crumb (back to that mode's global default):
                 standalone team    → Teams > <team>
                 expanded superteam → Teams > <superteam>
                 drilled child team → Teams > <superteam> > <team>
                 individual scope   → Individuals > <strider>
                 member of a team   → Teams > (<superteam> >) <team> > <strider>
               Every segment above the last links back up to its own level: the
               superteam to its child-teams list, the team to its full member
               list. In the global (unscoped) view it's the plain title. -->
          <span v-if="rootCrumb" class="lb-title lb-breadcrumb">
            <span
              class="lb-crumb-link lb-crumb-root"
              @click="goToRoot(rootCrumb.tab)"
              >{{ rootCrumb.label }}</span
            >
            <template v-if="superteam">
              <v-icon size="18" class="lb-crumb-sep">chevron_right</v-icon>
              <span
                :class="{ 'lb-crumb-link': team }"
                @click="team ? drillUp() : null"
                >{{ superteamName }}</span
              >
            </template>
            <template v-if="team">
              <v-icon size="18" class="lb-crumb-sep">chevron_right</v-icon>
              <span
                :class="{ 'lb-crumb-link': userId != null }"
                @click="userId != null ? clearMember() : null"
                >{{ teamName }}</span
              >
            </template>
            <!-- A strider's name arrives with the strides response the filter
                 fetches, so on a cold ?user= load this shows the generic label
                 until that lands (see Home.applyUserName). -->
            <template v-if="userId != null">
              <v-icon size="18" class="lb-crumb-sep">chevron_right</v-icon>
              <span>{{ userName || "Individual" }}</span>
            </template>
          </span>
          <span v-else class="lb-title">{{ title }}</span>
        </v-col>
        <v-col cols="auto">
          <!-- Mode switcher stays visible in every scope. The highlighted option
               is the active mode (root of the current drill); clicking it un-drills
               to that root, clicking another switches modes preserving each view. -->
          <div class="lb-toggle" role="tablist">
            <!-- Gradient thumb that slides to the active option. Its position and
                 width are measured from the active button (see updateThumb) since
                 the options are text-sized, not equal-width. -->
            <span class="lb-toggle__thumb" :style="thumbStyle"></span>
            <button
              v-for="(item, i) in tabItems"
              :key="item"
              ref="opts"
              type="button"
              role="tab"
              :aria-selected="activeMode === i"
              class="lb-toggle__opt"
              :class="{ 'lb-toggle__opt--active': activeMode === i }"
              @click="onModeClick(i)"
            >
              {{ item }}
            </button>
          </div>
        </v-col>
      </v-row>
      <!-- The measure columns are the sort control: clicking one re-orders every
           list from the server, always descending (see SORT_COLUMNS / sortBy).
           Rank and Name aren't sortable — rank IS the sort, and the API offers no
           ordering by name. -->
      <v-row no-gutters class="th" v-if="displayList.length">
        <v-col cols="1">No.</v-col>
        <v-col cols="4">Name</v-col>
        <v-col v-for="col in sortColumns" :key="col.key" :cols="col.cols">
          <button
            type="button"
            class="lb-sort"
            :class="{ 'lb-sort--active': sortBy === col.key }"
            :aria-pressed="sortBy === col.key ? 'true' : 'false'"
            :aria-label="`Sort by ${col.label}, highest first`"
            @click="sortByColumn(col.key)"
          >
            {{ col.label }}
            <!-- One direction only, so the arrow marks which column is sorted
                 rather than which way. Rendered on every heading and revealed on
                 hover, so the inactive ones read as clickable without shifting
                 the text when they become active. -->
            <v-icon size="14" class="lb-sort-icon">arrow_downward</v-icon>
          </button>
        </v-col>
      </v-row>
    </div>
    <div class="lb-rows" ref="scrollArea">
      <!-- transition-group so expanding a superteam reads as a movement: the rows
           in between fade out while its child teams slide up under it (FLIP move).
           The key must be the row's identity, not its index, or nothing moves —
           and it carries the type because a team and a superteam id share the
           same list. -->
      <transition-group
        v-if="displayList.length"
        tag="div"
        name="lb-row"
        class="lb-row-group"
        @before-leave="pinLeavingRow"
        @leave-cancelled="unpinRow"
      >
        <!-- The whole row is the click target (onRowClick routes by row type and
             mode), so the hit area matches what the row reads as. The one thing
             inside it that isn't part of that target is a superteam's chevron,
             which stops the event and toggles the expansion instead.
             It carries the button role rather than being a <button> element: the
             chevron inside it IS one, and a button nested in a button is invalid
             HTML that browsers recover from by dropping the inner control — which
             would cost the expander its own keyboard focus. So the row takes the
             role, the tab stop and the Enter/Space handling a button would give
             it, and the chevron stays a real button after it in the tab order. -->
        <v-row
          v-for="item in displayList"
          :key="`${item.type}:${item.id}`"
          :data-row-key="`${item.type}:${item.id}`"
          no-gutters
          class="tr"
          :class="{
            'tr--child': item.isChild,
            'tr--selected': isSelectedRow(item),
          }"
          role="button"
          tabindex="0"
          :aria-pressed="isSelectedRow(item) ? 'true' : null"
          @click="onRowClick(item)"
          @keydown.enter.prevent="onRowClick(item)"
          @keydown.space.prevent="onRowClick(item)"
        >
          <v-col cols="1">
            <span
              v-if="item.rank && item.rank <= medals.length"
              class="lb-medal"
              :aria-label="`Rank ${item.rank}`"
              >{{ medals[item.rank - 1] }}</span
            >
            <!-- A child of an expanded superteam that has no position in the
                 ranking (nothing collected in the filtered window) comes back with
                 rank: null; it shows a dash rather than a wrong number. -->
            <span class="lb-number" v-else>{{ item.rank || "—" }}</span>
          </v-col>
          <v-col cols="4" class="lb-name-col">
            <!-- Flex row: the icons never shrink, so a long name truncates and
                 the chevron stays visible. The name and its affiliation subtitle
                 stack as one shrinkable block between the two icons. -->
            <span class="lb-row-link">
              <v-icon
                v-if="item.type === 'superteam'"
                size="17"
                class="lb-row-icon"
                >$graph2</v-icon
              >
              <span class="lb-row-label">
                <span class="lb-row-name">{{ item.name }}</span>
                <span v-if="rowSubtitle(item)" class="lb-row-subtitle">{{
                  rowSubtitle(item)
                }}</span>
              </span>
              <!-- Show/hide a superteam's child teams in place. A control of the
                   list only: the dashboard's scope is the row body's job, so this
                   stops the row's click rather than sharing it. -->
              <button
                v-if="item.type === 'superteam'"
                type="button"
                class="lb-row-expander"
                :aria-expanded="isExpandedRow(item) ? 'true' : 'false'"
                :aria-label="`${
                  isExpandedRow(item) ? 'Hide' : 'Show'
                } the teams in ${item.name}`"
                @click.stop="toggleExpand(item)"
              >
                <v-icon
                  size="18"
                  class="lb-row-chevron"
                  :class="{ 'lb-row-chevron--open': isExpandedRow(item) }"
                  >expand_more</v-icon
                >
              </button>
            </span>
          </v-col>
          <v-col cols="2">{{ item.items_count | fcount }}</v-col>
          <v-col cols="2">{{ item.distance | fdistance }}</v-col>
          <v-col cols="3">{{ item.time_in_minutes | ftime }}</v-col>
        </v-row>
      </transition-group>
      <div v-else class="empty">
        <v-img
          max-height="150"
          max-width="150"
          :src="require('@/assets/home/empty.svg')"
        />
        <span>Sorry, there is no data yet</span>
      </div>
      <!-- Inline spinner for fetching the next page: sits below the current rows
           so the already-loaded list stays visible and scrollable instead of
           being covered by the blocking overlay (that's reserved for the initial
           / post-filter-change load, where there's nothing to show yet). -->
      <div v-if="loadingMore" class="lb-load-more">
        <v-progress-circular
          indeterminate
          color="primary"
          size="28"
          width="3"
        ></v-progress-circular>
      </div>
    </div>

    <v-overlay absolute :value="loading" :opacity="0.3">
      <v-progress-circular indeterminate color="primary"></v-progress-circular>
    </v-overlay>
  </div>
</template>
<script>
import { swrGet } from "../utils/swrCache";
import { jsonEqual } from "../utils/common";
import {
  LEADERBOARD_PAGE_SIZE,
  MERGED_TAB_ITEMS,
  DEFAULT_LEADERBOARD_SORT,
} from "../utils/constants";
import { mapState, mapActions } from "vuex";

const LEADERBOARD_URL = "/dashboards/leaderboard";

// The two modes of the switcher. Teams covers superteams too: the backend ranks
// them together, and a superteam row expands in place into its child teams.
const MODES = ["Individuals", "Teams"];

// The measure columns, in display order: the heading, the grid width it occupies,
// and the `sortBy` value the server orders that column by. One list drives both
// the headings and the sort control, so a column can't be shown without being
// sortable or ordered by a value the API doesn't take.
//
// Ordering is always descending — the API takes no direction param — and the same
// value applies to every list: the individuals ranking, a team's members, the
// merged teams+superteams ranking, a superteam's child teams. The one asymmetry is
// on the server: teams carry no points total, so on any team-side list it treats
// `points` as `items_count`. That's why no column here sorts by points; the panel
// shows no points column to click.
const SORT_COLUMNS = [
  { key: "items_count", label: "Items", cols: 2 },
  { key: "distance", label: "Distance", cols: 2 },
  { key: "time_in_minutes", label: "Time", cols: 3 },
];

// The page buckets the leaderboard keeps. `users` holds either a team's members or
// the global Individuals list (they can never be on screen at once); `teams` is the
// Teams ranking (teams and superteams interleaved by the server). The other two are
// both a superteam's member list, fetched the same way and kept apart because they
// answer for different superteams: `childTeams` is the list on screen while a
// superteam is navigated INTO, keyed on the scope; `resolvedChildren` supplies the
// rows an expanded superteam needs but the ranking hasn't loaded, keyed on whatever
// the chevron opened (see resolveMissingChildren).
const BUCKETS = ["users", "teams", "childTeams", "resolvedChildren"];

// The `tabItems` request param each global bucket is fetched with.
const TAB_ITEMS_BY_BUCKET = {
  users: "individuals",
  teams: MERGED_TAB_ITEMS,
};

// How many pages of the ranking the panel will walk looking for one superteam's
// row when coming back up out of it (see revealSuperteam) — the scroll it wants
// needs every row above that one loaded. At LEADERBOARD_PAGE_SIZE per page this
// covers the part of the ranking a user could plausibly scroll to; past it the
// walk gives up and the list stays at the top.
const REVEAL_MAX_PAGES = 15;

// The same bound for the other silent walk: paging the ranking forward to where
// an expanded group ends (see fillPastGroup). That walk chases a rank, not a row,
// and the rank can be arbitrarily deep — a group whose last child sits at 5000
// would otherwise page the whole ranking one request at a time. Giving up leaves
// the list ending at the group, which is the same view an un-filled group shows;
// it never costs a child team, since those come from the by-id lookup instead
// (resolveMissingChildren).
const FILL_MAX_PAGES = 15;

// How many times a bucket will ask for the same offset before giving up on it.
// A page that errors, or that comes back without the key its bucket pages on,
// deliberately leaves the cursor where it was so the offset can be retried — see
// applyPage. Nothing else stops that retry: the silent walks chain into
// themselves, so a page that never succeeds is asked for again at the speed of
// the network. This is the terminator for that, and it is per offset rather than
// per chain, so a walk making real progress is never cut short.
const MAX_OFFSET_ATTEMPTS = 5;

// The buckets a mode can warm ahead of time: the global, unscoped lists. The
// superteam-scoped buckets are deliberately absent — their params depend on which
// superteam is expanded, so there's nothing to prefetch.
const PREFETCHABLE_BUCKETS = ["users", "teams"];

// Fallback kind for rows that don't state their own `type`. The ranking's rows
// always do (that's what lets a click route by the row rather than by the list it
// came from); the scoped lists are single-kind, so the bucket names it for them.
const ROW_TYPE_BY_BUCKET = {
  users: "user",
  teams: "team",
  childTeams: "team",
  resolvedChildren: "team",
};

// A list scoped to one superteam holds that superteam's teams. A superteam row in
// one means the `superteam=` param was ignored, and rendering it would put a
// chevron — an expander for a group inside a group — in a list that claims to be a
// single superteam's teams. lookupSlug validates a response against the row's own
// type the same way.
//
// Applied where the rows are READ, not where the page is written: applyPage puts
// each page at its server offset, so dropping a row on the way in would leave the
// array shorter than the cursor that addresses it and every later splice would
// land in the wrong slice.
const withoutSuperteams = (rows) =>
  rows.filter((row) => row.type !== "superteam");

const emptyByBucket = (value) =>
  BUCKETS.reduce((acc, key) => ({ ...acc, [key]: value() }), {});

export default {
  data() {
    return {
      tab: 0,
      // Medal emoji for the top ranks; array length sets how many ranks get a
      // medal vs a plain number, so add/remove entries to move the cutoff.
      medals: ["🥇", "🥈", "🥉"],
      // The column every list is ordered by, sent to the server as `sortBy`.
      // Component state, not a store filter: it changes the order of the
      // leaderboard's own lists and nothing else on the dashboard, and it is
      // deliberately out of the URL — a shared link carries the scope and the
      // date/country window, not how the panel happened to be sorted.
      sortBy: DEFAULT_LEADERBOARD_SORT,
      users: [],
      // The Teams ranking as the server pages it: teams and superteams
      // interleaved, each row carrying its own `type` and `rank`.
      teams: [],
      // Member teams of the selected superteam (ranked), and children of the
      // expanded superteam looked up by id (see resolveMissingChildren) — kept in
      // their own arrays so they never collide with the Individuals/Teams lists or
      // their pagination.
      childTeams: [],
      resolvedChildren: [],
      // `loading` blocks the whole panel with an overlay (initial / reset load,
      // when there's nothing to show). `loadingMore` is the inline spinner under
      // the rows while fetching the next page, so the current list stays usable.
      loading: false,
      loadingMore: false,
      // Left/width of the sliding thumb, measured from the active option button
      // (see updateThumb) since options are text-sized rather than equal-width.
      thumbLeft: 0,
      thumbWidth: 0,
      // Gates the thumb's slide transition until it has been measured once, so it
      // doesn't animate in from the track's left edge on first render.
      thumbReady: false,
      // Bumped whenever a bucket is reset (a filter or scope change). A fetch
      // captures it and may only apply its result while it still matches, so a
      // request the user has navigated away from is discarded rather than
      // painted. Pages of the same list no longer supersede each other, since
      // they write to different offsets and both belong on screen.
      generation: 0,
      // Per-bucket pagination cursors and end-of-data flags, so switching modes
      // restores each list where it left off instead of wiping and refetching.
      offsets: emptyByBucket(() => 0),
      exhausted: emptyByBucket(() => false),
      // Consecutive failed attempts at one offset, per bucket. A failed page
      // leaves the cursor alone so the offset can be retried, which is what makes
      // a transient error recoverable — and what would otherwise retry forever.
      // Cleared as soon as a page applies, so only a page making no progress is
      // ever counted. See notePageFailure and MAX_OFFSET_ATTEMPTS.
      pageFailures: emptyByBucket(() => ({ offset: null, count: 0 })),
      limit: LEADERBOARD_PAGE_SIZE,
      // Per-mode saved view, keyed by mode index (0 Individuals, 1 Teams). Each
      // entry is the full leaderboard state for that mode at the moment you
      // switched away: scope, every bucket with its pagination cursors, and scroll
      // offset. Restored on switching back so a drilled-in view persists. Cleared
      // when the date/country data filters change (see the dataFilterConditions
      // watcher); a scope change / drill leaves other modes' snapshots intact.
      modeSnapshots: { 0: null, 1: null },
      // The superteam whose child teams are showing in place, or null. Expansion
      // is a control of THIS list and nothing else: the chevron owns it, and it
      // is deliberately independent of the superteam scope, which only
      // highlights a row. The one non-chevron writer is a superteam scoped from
      // outside the panel (see the superteam watcher).
      expandedId: null,
      // Whether the superteam scope is one the panel has navigated INTO (its
      // child teams are the list on screen) rather than a row selected in the
      // ranking. Set by where the scope came from: a shared URL or the search
      // field navigates, a row-body click selects. See `mode`.
      superteamDrilled: false,
      // Set by a superteam row-body click for the duration of its own dispatch,
      // so the watcher can tell that scope change from one arriving from search
      // or a URL — only the latter navigates into the superteam.
      scopingFromRow: false,
      // The superteam to bring into view in the ranking on the way back up out of
      // it, and how many pages have been walked looking for its row. See
      // revealSuperteam.
      pendingReveal: null,
      revealPages: 0,
      revealing: false,
      // Set for the duration of a mode switch. While true the scope watchers
      // no-op, so applying a saved scope re-scopes the rest of the dashboard
      // without the normal list-wiping reload — the switch machinery restores
      // the leaderboard's lists/scroll itself.
      isSwitchingMode: false,
      // Buckets marked stale this tick, and whether their reload is already
      // scheduled. A composite scope change (selectUser sets userId and clears
      // team) touches several watchers, so the resets are collected and one
      // reload is issued for the tick.
      pendingReset: null,
      resetQueued: false,
      // Whether the expanded superteam's missing children are still being looked
      // up. While they are, the rows after the expanded group are withheld — see
      // expandedRows.
      resolvingChildren: false,
      // Whether a page of that lookup is in flight. The lookup is keyed on the
      // expanded superteam's id alone, so unlike a by-id lookup there's no list to
      // hold still across the chain. See resolveMissingChildren.
      resolvingChildrenPage: false,
      // Whether a page is already in flight for the silent paging that walks the
      // ranking down to where an expanded group ends, and how many pages it has
      // spent (bounded by FILL_MAX_PAGES). See fillPastGroup.
      fillingPastGroup: false,
      fillPages: 0,
      // The row an in-place expand/collapse is holding still, as its key and the
      // viewport position to keep it at, until the morph has settled. See
      // anchorRow.
      pendingAnchor: null,
      // Scroll offset per drill level (see levelKey), so returning to a level
      // you've already been at puts the rows back where you left them instead of
      // at the top. Cleared with the mode snapshots when the date/country filters
      // change, since every list's contents change with them.
      scrollByLevel: {},
      // The level the rows on screen belong to. Tracked eagerly rather than read
      // from the scope, because by the time a watcher runs the scope already holds
      // its new value — this is what the outgoing scroll gets filed under.
      currentLevelKey: null,
    };
  },
  computed: {
    title() {
      return this.team ? "Team Leaderboard" : "Stridy Leaderboard";
    },
    // The breadcrumb's clickable root: the global mode the current scope
    // descends from. null in the global view (no breadcrumb — the plain title
    // shows instead). `tab` is the mode index goToRoot lands on. Every team-side
    // scope roots under Teams, superteams included; an individual scope roots
    // under Individuals. The labels come from MODES so the root crumb always
    // reads the same as the toggle option it returns to. Team-side is checked
    // first: the two scopes are mutually exclusive in the store, but a drilled
    // team's member list belongs to Teams either way.
    rootCrumb() {
      if (this.superteam || this.team) return { label: MODES[1], tab: 1 };
      if (this.userId != null) return { label: MODES[0], tab: 0 };
      return null;
    },
    tabItems() {
      return MODES;
    },
    sortColumns() {
      return SORT_COLUMNS;
    },
    // Which list is on screen:
    //   team set                       → "members"    (a team's members)
    //   superteam navigated into       → "childTeams" (its own ranked children)
    //   otherwise                      → "global"     (the ranking / Individuals)
    //
    // A superteam scope means one of two different things, and which one depends
    // on where it came from (`superteamDrilled`):
    //   - clicked on its row here → a selection. The ranking stays on screen with
    //     that row highlighted, exactly as selecting an individual does.
    //   - from a shared URL or the search field → a navigation into it. There was
    //     no row on screen to highlight, so the panel shows the superteam's own
    //     child teams, whether or not a loaded page happens to hold its row.
    mode() {
      if (this.team) return "members";
      if (this.superteam != null && this.superteamDrilled) return "childTeams";
      return "global";
    },
    // Whether the merged Teams ranking is the list on screen — the only list an
    // in-place expansion can apply to.
    showsRanking() {
      return this.mode === "global" && this.activeMode === 1;
    },
    // The toggle's highlighted option: the root mode the current scope descends
    // from. With teams and superteams merged, every team-side scope roots under
    // Teams; a selected individual roots under Individuals; with no scope it
    // follows the user's last toggle choice (`tab`). Note this can differ from
    // `mode`: a drilled child team has mode "members" but activeMode 1.
    activeMode() {
      if (this.superteam != null) return 1;
      if (this.team != null) return 1;
      if (this.userId != null) return 0;
      return this.tab;
    },
    // Whether the active mode is scoped below its global root.
    isDrilled() {
      return this.superteam != null || this.team != null || this.userId != null;
    },
    // The expanded superteam's own row in the ranking, when it's loaded. Its
    // presence is what makes the expansion an in-place morph of the list already
    // on screen. null when the ranking isn't what's on screen, or when the
    // superteam isn't in the loaded pages (opened by a scope restored from a URL
    // before its page has arrived) — then there's nothing to morph, and the whole
    // apparatus below goes inert until there is.
    expandedSuperteamRow() {
      if (this.expandedId == null || !this.showsRanking) return null;
      return (
        this.teams.find(
          (row) => row.type === "superteam" && row.id === this.expandedId
        ) || null
      );
    },
    isSuperteamExpanded() {
      return this.expandedSuperteamRow != null;
    },
    // The rank the expanded superteam's group ends at: its lowest-ranked child,
    // counting the ones fetched out of band. Where the group ends is a RANK, not a
    // position in the loaded pages: a child can rank far past everything paged in
    // (see resolveMissingChildren), and every non-child row above that rank is
    // still an in-between row. Cutting by index instead resumed the list right
    // after the last child that happened to be loaded — at 26 when the group's own
    // last child ranks 300.
    //   0        — nothing expanded
    //   Infinity — the child lookup is in flight, so the end isn't known yet;
    //              nothing after the head can be called "below the group", and the
    //              whole tail is withheld rather than showing rows the arriving
    //              children are about to displace
    groupEndRank() {
      const rows = this.teams;
      const head = this.expandedSuperteamRow;
      if (!head) return 0;
      if (this.resolvingChildren) return Infinity;
      const headIndex = rows.indexOf(head);
      const childIds = new Set(head.childTeamIds || []);
      let end = 0;
      rows.forEach((row, i) => {
        if (i > headIndex && row.type === "team" && childIds.has(row.id))
          end = Math.max(end, this.rankAt(row, i));
      });
      // A child with no position in the ranking at all ranks nowhere, so it can't
      // move the cut-off; it just sits at the end of the group.
      this.missingChildRows.forEach((row) => {
        if (row.rank != null) end = Math.max(end, row.rank);
      });
      return end;
    },
    // The ranking with the expansion applied in place: nothing is re-ordered
    // or re-numbered and nothing is fetched — rows are only hidden. Kept are the
    // rows ABOVE the expanded superteam, the superteam itself, its child teams
    // wherever they rank, and everything BELOW the last of those children. The
    // non-child rows in between are dropped, which is what closes the gap and
    // brings the children up under their parent.
    expandedRows() {
      const rows = this.teams;
      const headIndex = rows.indexOf(this.expandedSuperteamRow);
      const childIds = new Set(rows[headIndex].childTeamIds || []);
      const isChildAt = (row, i) =>
        i > headIndex && row.type === "team" && childIds.has(row.id);
      const groupEndRank = this.groupEndRank;
      const kept = [];
      let tailAt = -1;
      rows.forEach((row, i) => {
        const isChild = isChildAt(row, i);
        const rank = this.rankAt(row, i);
        const isTail = i > headIndex && !isChild && rank > groupEndRank;
        if (i > headIndex && !isChild && !isTail) return;
        if (isTail && tailAt === -1) tailAt = kept.length;
        kept.push({ ...row, rank, isChild });
      });
      if (!this.missingChildRows.length) return kept;
      // Children with no row in the ranking go after the ranked ones, at the end of
      // the group (see missingChildRows).
      kept.splice(
        tailAt === -1 ? kept.length : tailAt,
        0,
        ...this.missingChildRows
      );
      return kept;
    },
    // Children of the expanded superteam that the loaded pages of the ranking
    // don't cover — the ones with no row on screen to un-hide. Only the COUNT is
    // used now (as "is there anything to fetch"): the fetch itself asks for the
    // superteam's whole member list rather than these ids. See
    // resolveMissingChildren.
    missingChildIds() {
      const head = this.expandedSuperteamRow;
      if (!head || !head.childTeamIds) return [];
      const loaded = new Set(
        this.teams.filter((row) => row.type === "team").map((r) => r.id)
      );
      return head.childTeamIds.filter((id) => !loaded.has(id));
    },
    missingChildCount() {
      return this.missingChildIds.length;
    },
    // Whether the member-list lookup has finished. It's a paged list like any
    // other, so it ends where they all do: on the page that comes back short.
    childrenResolved() {
      return this.exhausted.resolvedChildren;
    },
    // The expanded superteam's children that aren't already in the loaded pages,
    // taken from its member list, so an expanded superteam is never silently
    // missing a team. They go at the end of the group: they rank below every child
    // that IS in the loaded pages. Each carries its true global rank, or null for a
    // team with no position in the ranking at all (nothing collected in the
    // filtered window) — that one shows a dash.
    //
    // A member list that predates the `rank` field on `superteam=<id>` rows
    // leaves every row here at null: the children still all appear, each with a dash
    // instead of a number, and `groupEndRank` learns nothing from them.
    missingChildRows() {
      const head = this.expandedSuperteamRow;
      if (!head || !this.missingChildCount || this.resolvingChildren) return [];
      const ranked = new Set(
        this.teams.filter((row) => row.type === "team").map((r) => r.id)
      );
      return withoutSuperteams(this.resolvedChildren)
        .filter(
          (row) =>
            !ranked.has(row.id) && (head.childTeamIds || []).includes(row.id)
        )
        .map((row) => ({
          ...row,
          isChild: true,
          rank: row.rank != null ? row.rank : null,
        }));
    },
    // The navigated-into superteam's children as rendered — see
    // withoutSuperteams for why the filter lives here and not in applyPage.
    childTeamRows() {
      return withoutSuperteams(this.childTeams);
    },
    displayList() {
      if (this.isSuperteamExpanded) return this.expandedRows;
      if (this.mode === "members") return this.withRanks(this.users);
      if (this.mode === "childTeams")
        return this.withPositions(this.childTeamRows);
      // Global: the list the highlighted toggle option names. activeMode rather
      // than `tab`, since `tab` can still be 0 under a team-side scope (a drill
      // forces it there and drill-up doesn't restore it) — see listKey.
      if (this.activeMode === 0) return this.withRanks(this.users);
      return this.withRanks(this.teams);
    },
    // Position/size the sliding thumb from the measured active option (see
    // updateThumb), so it tracks each text-sized pill's own width.
    thumbStyle() {
      return {
        left: `${this.thumbLeft}px`,
        width: `${this.thumbWidth}px`,
        transition: this.thumbReady ? null : "none",
      };
    },
    // Which bucket the current scope pages through. Members mode and the global
    // Individuals tab both feed `users`; they can never coexist because a scope
    // change resets that bucket.
    listKey() {
      if (this.mode === "members") return "users";
      if (this.mode === "childTeams") return "childTeams";
      // An in-place expansion is still the Teams ranking — it keeps paging
      // `teams`, so scrolling past the last child loads more of the ranking.
      // `tab` is deliberately not consulted: it sits at 0 after a drill into a
      // team and is only restored on the way out to global, so a superteam scope
      // would otherwise page the Individuals bucket under a Teams toggle.
      return ["users", "teams"][this.activeMode];
    },
    // Identity of the list currently being navigated, used to file and restore
    // scroll offsets as you drill in and back out (see changeLevel). The scope
    // names a drilled level; `tab` only tells the two global lists apart, and is
    // deliberately out of the key elsewhere — a drill forces it to 0 and doesn't
    // restore it on the way back up, so a scoped level keyed by it wouldn't match
    // itself; the mode index it settles at (activeMode) is what names the two
    // global lists apart. `userId` is absent too: selecting someone doesn't
    // change the list, so it isn't a level of its own and must not move the
    // scroll. Neither is a superteam scope whose row is in the ranking, for the
    // same reason — it highlights a row and leaves the list alone, so it stays
    // the global level. Only the stand-in child list is a level of its own.
    levelKey() {
      if (this.mode === "global") return `global:${this.activeMode}`;
      return `scope:${this.superteam}:${this.team}`;
    },
    ...mapState([
      "startDay",
      "endDay",
      "country",
      "team",
      "superteam",
      "superteamName",
      "superteamSlug",
      "teamName",
      "teamSlug",
      "userId",
      "userName",
    ]),
    // The only filters every list's data depends on: the date window and the
    // country. A change to either invalidates every bucket AND every saved mode
    // view. The scope dimensions are deliberately absent. A team/superteam only
    // picks WHICH list is on screen, so its own watcher resets just the bucket it
    // owns and the merged Teams list survives an expand/collapse; and selecting
    // an individual changes no list at all — it narrows the map and stats while
    // the ranking they were picked from stays put, so the rest of the list stays
    // there to compare against and to pick someone else from. Both are per-mode
    // navigation besides: drilling in one mode leaves another's snapshot valid.
    dataFilterConditions() {
      return [this.startDay, this.endDay, this.country].join();
    },
  },
  watch: {
    team(newTeamId) {
      if (this.isSwitchingMode) return;
      // A drill hides the tabs and shows the team's member list, so `tab` goes to
      // 0; clearing the team puts it on Teams. Every team-side scope roots under
      // Teams (`rootCrumb`), whether a parent superteam remains or not, so that's
      // the list to come back to — and `tab` is what names the global list once
      // the whole scope is gone. It can't be restored to whatever it was before
      // the drill: a team scope can arrive with the panel sitting on Individuals
      // (from the search field, or from a slug link that resolves after mount),
      // and putting the Individuals ranking back contradicts the Teams breadcrumb
      // the scope carried the whole time.
      this.tab = newTeamId ? 0 : 1;
      this.changeLevel();
      // Only the members list depends on the team scope.
      this.queueReset(["users"]);
    },
    superteam(newId) {
      if (this.isSwitchingMode) return;
      // Where the scope came from decides what the panel does with it (see
      // `mode`): a row-body click selects that row, anything else — search, a
      // shared URL — navigates into the superteam. The navigation also opens the
      // group in the ranking underneath, so coming back up lands on an expanded
      // superteam rather than on a row that gives no sign of where you were.
      const fromRow = this.scopingFromRow;
      this.scopingFromRow = false;
      this.superteamDrilled = newId != null && !fromRow;
      if (this.superteamDrilled) this.expandedId = newId;
      // The superteam scope going with no child team left under it exits to
      // global, and a superteam lives on the Teams side of the switcher — so
      // that's the root to land on. `tab` has to be told: it can still be at the
      // 0 a drill forced on it, or the 0 the panel was showing when the scope
      // landed from search or a link, and with the scope gone `tab` is what names
      // the global list.
      if (newId == null && !this.team) this.tab = 1;
      // A no-op unless the child list went up or came down; a scope that only
      // highlights a row leaves the level, and so the scroll, alone.
      this.changeLevel();
      // The child list is the only list a superteam scope owns. Reset it only
      // when it actually holds something: an empty reset still bumps the
      // generation, which would discard a page of the ranking in flight and leave
      // its spinner stuck on.
      this.queueReset(
        this.childTeams.length || this.offsets.childTeams ? ["childTeams"] : []
      );
    },
    // The expanded superteam changed (or the group was closed). Nothing here
    // touches the store, so this is a re-render of the rows already on screen:
    // no refetch of the list, no level change, no scroll of its own — only the
    // by-id lookup of children the loaded pages don't cover.
    expandedId() {
      if (this.isSwitchingMode) return;
      // The bucket still holds the previously expanded superteam's children, and
      // its cursor with them. Reset directly rather than via queueReset, which
      // would also reload the list on screen.
      //
      // Without the generation bump: this bucket's params are keyed on
      // expandedId, so a page for the superteam just collapsed already fails
      // fetchPages' params check and is dropped there. Bumping would additionally
      // discard whatever else is in flight — a ranking "load more" among them —
      // and nothing here replaces it.
      if (this.resolvedChildren.length || this.offsets.resolvedChildren)
        this.resetLists(["resolvedChildren"], { invalidate: false });
      // A fresh group gets a fresh page budget for the walk to its tail.
      this.fillPages = 0;
      // Children ranked below the loaded pages have no row to un-hide yet, and an
      // expand fetches nothing on its own — so start the lookup here instead of
      // leaving them invisible until the user scrolls.
      this.$nextTick(this.settleExpansion);
    },
    // The ranking coming back on screen re-arms an expansion that went inert while
    // another list was showing, and is where a queued reveal starts walking. This
    // runs after the scope watchers above, so their resets — which would discard
    // the walk's first page — have been queued by the time it does.
    showsRanking(shows) {
      if (!shows || this.isSwitchingMode) return;
      this.$nextTick(this.settleExpansion);
    },
    // A date/country change invalidates every bucket and every saved mode view.
    // Scope changes (a drill) reload only the bucket they own and preserve
    // other modes' snapshots, so switching back restores their scroll and rows.
    dataFilterConditions() {
      this.modeSnapshots = { 0: null, 1: null };
      this.scrollByLevel = {};
      if (this.isSwitchingMode) return;
      this.queueReset(BUCKETS);
    },
    // Every list's order comes from the server, so a sort change invalidates every
    // bucket and every saved mode view exactly as a date/country change does — a
    // snapshot restored after it would put rows back in the old order. The saved
    // scroll offsets go with them: a level's offset was the position of rows that
    // are about to be somewhere else. Any pending reveal/anchor is dropped for the
    // same reason — both name a row position in a list that no longer exists.
    //
    // The expansion stays: which superteam is open is independent of the order its
    // children come back in.
    sortBy() {
      this.modeSnapshots = { 0: null, 1: null };
      this.scrollByLevel = {};
      this.pendingReveal = null;
      this.pendingAnchor = null;
      this.queueReset(BUCKETS);
      this.$nextTick(this.resetScroll);
    },
    tab() {
      if (this.isSwitchingMode) return;
      this.changeLevel();
      this.$nextTick(this.updateThumb);
      // A tab already populated (or known empty) for the current filters is
      // restored as-is: flipping between tabs costs zero requests.
      if (this.needsInitialLoad()) this.loadData();
    },
    // An expand withholds the rows after the group while the superteam's missing
    // children are looked up, so the list's height changes twice more after the
    // click — once when the tail goes and once when it comes back with them. Put
    // the anchored row back when that has settled, and let it go.
    resolvingChildren(resolving) {
      if (resolving || !this.pendingAnchor) return;
      this.$nextTick(this.settleAnchor);
    },
    // Each page the silent paging brings in lengthens the list, which is what lets
    // a row near its end be scrolled further up. The chain sets this false between
    // pages, so this fires per page and only releases the anchor on the last one.
    fillingPastGroup(filling) {
      if (filling || !this.pendingAnchor) return;
      this.$nextTick(this.settleAnchor);
    },
    // The highlighted option is activeMode; remeasure the thumb when it changes
    // (scope drill/un-drill or a global tab switch) once the DOM settles.
    activeMode() {
      this.$nextTick(this.updateThumb);
    },
  },
  mounted() {
    this.currentLevelKey = this.levelKey;
    // A scope restored from the URL is already in the store before this panel
    // exists, so the watcher never sees it arrive. Treat it as the navigation it
    // is, for the reasons the watcher does.
    if (this.superteam != null) {
      this.superteamDrilled = true;
      this.expandedId = this.superteam;
    }
    this.loadData();
    this.prefetchInactiveTabs();
    this.addScrollListener();
    this.$nextTick(this.updateThumb);
  },
  // Where every row sat in the list the user is still looking at. A row leaving the
  // list is pinned back to its entry here (see pinLeavingRow); it has to be taken
  // before the patch, since by the time a leave transition starts the rows around it
  // have already moved or left the flow. Held as a plain instance field rather than
  // in data(): it's written on every render and read by none of them.
  beforeUpdate() {
    this.rowTopsBeforeRender = this.measureRowTops();
  },
  beforeDestroy() {
    this.removeScrollListener();
  },
  methods: {
    ...mapActions([
      "drillTeam",
      "selectTeam",
      "selectSuperteam",
      "selectUser",
      "selectMember",
      "clearSuperteam",
      "updateTeam",
      "updateTeamName",
      "updateTeamSlug",
      "updateSuperteam",
      "updateSuperteamName",
      "updateSuperteamSlug",
      "updateUserId",
      "updateUserName",
    ]),
    // A row's rank is its position in the list it's shown in — unless the row
    // states its own, which every row of the server-ranked Teams list does. The
    // fallback numbers the scoped lists (a team's members, a superteam's own
    // member list), which are ranked 1..n within their scope.
    rankAt(row, index) {
      return row.rank != null ? row.rank : index + 1;
    },
    withRanks(list) {
      return list.map((item, i) => ({ ...item, rank: this.rankAt(item, i) }));
    },
    // Number a list by position, ignoring any rank its rows carry. The drilled-in
    // superteam view reads as "these are Alpha's teams, best first", so 1..n within
    // the superteam is the number that belongs there — and it's what keeps the
    // podium meaningful. Those same rows now also carry a GLOBAL rank — their
    // position in the merged ranking — which is the right number for the
    // in-place expansion, where they sit among the ranking's other rows; using it
    // here too would leave a superteam whose best team ranks 40th showing no medal
    // at all.
    withPositions(list) {
      return list.map((item, i) => ({ ...item, rank: i + 1 }));
    },
    isExpandedRow(item) {
      return item.type === "superteam" && this.expandedId === item.id;
    },
    // The muted line under a row's name: what the row belongs to. A team names its
    // parent superteam (`superteamName`), a strider the teams they strided with in
    // the filtered window (`teamNames`, biggest contribution first, so a subtitle
    // too long for the column drops the least relevant name).
    //
    // Both fields are absent wherever the affiliation would be the same on every
    // row and so already stated in the breadcrumb: the server omits `teamNames`
    // from a team's member list and `superteamName` from a superteam's own member
    // list. The one case it can't know about is a child row of an in-place
    // expansion — that row came from the merged ranking, where the field belongs —
    // so the parent it would name is the superteam directly above it.
    // eslint-disable-next-line no-unused-vars
    rowSubtitle(item) {
      // Both subtitles are off for now — `item` stays in the signature for the
      // commented-out branches below.
      // if (item.type === "user") return (item.teamNames || []).join(", ");
      // if (item.type === "team" && !item.isChild)
      //   return item.superteamName || "";
      return "";
    },
    // A row the dashboard is scoped to, highlighted in place. The two row types
    // that scope without navigating: a strider and a superteam. Neither hides
    // anything, so the row is the only thing marking what's scoped. Matched on
    // the type as well as the id, since the three kinds share an id space.
    isSelectedRow(item) {
      if (item.type === "user")
        return this.userId != null && this.userId === item.id;
      if (item.type === "superteam")
        return this.superteam != null && this.superteam === item.id;
      return false;
    },
    // Clicking a row changes scope, routed by the row's own type and the current
    // mode. Panels (incl. this leaderboard, via its watchers) re-fetch off the
    // store, so we only set the scope here; the composite actions enforce the
    // mutual-exclusion rules.
    //   superteam row       → scope the dashboard to it, highlighting its row
    //   team row, a child   → drill into it, scoping the superteam it sits under
    //   team row, ranking   → standalone team scope
    //   team row, stand-in  → drill into that child team (keep the superteam)
    //   member row          → that strider WITHIN the team (selectMember)
    //   Individuals row     → that strider dashboard-wide (selectUser)
    // The rows that scope without navigating toggle: clicking the selected one
    // deselects. Showing a superteam's child teams is NOT here — that's the
    // chevron's job (toggleExpand), and it leaves the scope alone.
    onRowClick(item) {
      if (item.id == null) {
        console.warn("Leaderboard: row has no id, cannot filter", item);
        return;
      }
      // `slug` rides along for the team/superteam scopes so the URL can be the
      // human-readable path (see utils/urlFilters.js); selectUser ignores it —
      // individuals have no slug.
      const payload = { id: item.id, name: item.name, slug: item.slug || null };
      if (item.type === "superteam") {
        if (this.superteam === item.id) return this.deselectSuperteam();
        return this.scopeSuperteamFromRow(payload);
      }
      if (this.mode === "members") {
        // A member of the scoped team. selectMember keeps that team, so the
        // dashboard narrows to this strider's strides within it and the
        // leaderboard stays on the member list rather than jumping to the
        // global Individuals ranking.
        if (this.userId === item.id) return this.clearMember();
        return this.selectMember(payload);
      }
      if (this.mode === "global" && this.activeMode === 0) {
        // The global Individuals ranking: a dashboard-wide individual scope,
        // which selectUser makes exclusive with the team side.
        if (this.userId === item.id) return this.clearMember();
        return this.selectUser(payload);
      }
      // A child team of the stand-in list keeps the superteam it's a list of.
      if (this.mode === "childTeams") return this.drillTeam(payload);
      // A child row of an expanded group reads as part of the superteam above it,
      // so it drills into that superteam as well — the parent may only have been
      // expanded, never scoped. Both scopes together give the full breadcrumb;
      // the requests are the same either way (Home.vue sends `superteam` only
      // when no child team is drilled).
      const parent = item.isChild && this.expandedSuperteamRow;
      if (parent) {
        this.scopeSuperteamFromRow({
          id: parent.id,
          name: parent.name,
          slug: parent.slug || null,
        });
        return this.drillTeam(payload);
      }
      return this.selectTeam(payload);
    },
    // Re-clicking the scoped superteam's row unscopes it, as an individual's row
    // does. The group it opened stays open — that's the chevron's to close. The
    // Teams tab comes back via the `superteam` watcher, which does that for every
    // way the scope can be dropped.
    deselectSuperteam() {
      this.clearSuperteam();
    },
    // Scope the dashboard to a superteam from a click on its own row (or on one
    // of its children). Flagged so the watcher leaves the expansion as the user
    // set it — only a scope arriving from outside the panel opens the group.
    scopeSuperteamFromRow(payload) {
      this.scopingFromRow = true;
      this.selectSuperteam(payload);
    },
    // The chevron: show or hide a superteam's child teams in place. A control of
    // this list alone — the dashboard's scope is untouched, so nothing is
    // refetched and no other panel moves.
    //
    // The morph rewrites the rows above the clicked one whenever another
    // superteam was open (everything it was hiding comes back), so keeping
    // scrollTop would move the list under the cursor. Anchor on the row that was
    // clicked instead — see anchorRow.
    toggleExpand(item) {
      this.anchorRow(`${item.type}:${item.id}`, () => {
        this.expandedId = this.expandedId === item.id ? null : item.id;
      });
    },
    // Breadcrumb "up" from a drilled team back to the superteam's child-teams
    // list: just drop the team (the parent superteam stays). drillTeam clears any
    // selected member with it — that strider was scoped within the team we're
    // leaving.
    drillUp() {
      this.drillTeam({ id: null, name: null, slug: null });
    },
    // Breadcrumb "up" from a selected strider back to the list they were picked
    // from, and the reclick-to-deselect on their row. Only the individual goes;
    // whatever team scope it sat inside stays.
    clearMember() {
      this.updateUserId(null);
      this.updateUserName(null);
    },
    // Return any scoped view to a mode's global root. Used by both the
    // breadcrumb root crumb and clicking the active toggle option — the spec
    // makes them identical. A selected individual always goes — it's either the
    // Individuals scope itself or a strider selected inside the team scope Teams
    // is about to clear. Teams then clears the team side (both team and
    // superteam, since they're one mode). Sets `tab` so global mode shows that
    // mode's list.
    goToRoot(mode) {
      // Coming up out of a superteam the panel had navigated into, the ranking has
      // to say where that superteam sits. Its row was never on screen — the list
      // being left is the superteam's own children — so there's no scroll offset
      // to go back to, and landing at the top of the ranking gives no sign of
      // where the user just was. Note it before the scope goes; revealSuperteam
      // takes it from here.
      if (mode === 1 && this.superteamDrilled && this.superteam != null)
        this.queueReveal(this.superteam);
      this.tab = mode;
      // The whole Individuals scope, and the strider half of a member selection.
      this.clearMember();
      if (mode === 0) return;
      if (this.superteam != null) {
        // clearSuperteam drops the drilled child team too.
        this.clearSuperteam();
      } else {
        this.updateTeam(null);
        this.updateTeamName(null);
        this.updateTeamSlug(null);
      }
      if (this.pendingReveal != null) this.$nextTick(this.revealSuperteam);
    },
    // Toggle click. Clicking the already-active mode un-drills to its root (same
    // as the breadcrumb) when scoped, or does nothing at the global root. Clicking
    // a different mode switches while preserving both modes' views.
    onModeClick(i) {
      if (i === this.activeMode) {
        if (this.isDrilled) this.goToRoot(i);
        return;
      }
      this.switchMode(i);
    },
    // A click on a column heading: order every list by that column. Descending is
    // the only direction the API offers, so re-clicking the active column is a
    // no-op rather than a direction toggle — it would otherwise refetch the list
    // that is already on screen.
    sortByColumn(key) {
      if (key === this.sortBy) return;
      this.sortBy = key;
    },
    // Switch to a different mode: stash the current mode's view, then restore the
    // target's saved view if present, else open its fresh global root.
    switchMode(i) {
      this.isSwitchingMode = true;
      this.snapshotMode(this.activeMode);
      const snap = this.modeSnapshots[i];
      if (snap) this.restoreSnapshot(snap);
      else this.goToModeRootFresh(i);
    },
    // The buckets a mode reads. Teams spans three: the ranking itself plus the two
    // lists an expanded superteam draws its children from.
    bucketsForMode(mode) {
      if (mode === 0) return ["users"];
      return ["teams", "childTeams", "resolvedChildren"];
    },
    // Capture a mode's full view: its scope, every bucket with its pagination
    // cursors, and the scroll offset. Copies of the lists are taken so later
    // edits to the live arrays don't mutate the snapshot. All buckets are stored
    // rather than just the active one — a mode can read several, and the extra
    // arrays are one page each.
    snapshotMode(mode) {
      const lists = {};
      BUCKETS.forEach((key) => {
        lists[key] = this[key].slice();
      });
      this.modeSnapshots[mode] = {
        scope: {
          userId: this.userId,
          userName: this.userName,
          team: this.team,
          teamName: this.teamName,
          teamSlug: this.teamSlug,
          superteam: this.superteam,
          superteamName: this.superteamName,
          superteamSlug: this.superteamSlug,
        },
        tab: this.tab,
        expandedId: this.expandedId,
        superteamDrilled: this.superteamDrilled,
        lists,
        offsets: { ...this.offsets },
        exhausted: { ...this.exhausted },
        scrollTop: this.$refs.scrollArea ? this.$refs.scrollArea.scrollTop : 0,
      };
    },
    // Commit an exact scope to the store. The snapshot is already internally
    // consistent, so we set each field directly rather than via the composite
    // actions (which enforce mutual exclusion by clearing siblings).
    applyScope(scope) {
      this.updateUserId(scope.userId);
      this.updateUserName(scope.userName);
      this.updateTeam(scope.team);
      this.updateTeamName(scope.teamName);
      this.updateTeamSlug(scope.teamSlug);
      this.updateSuperteam(scope.superteam);
      this.updateSuperteamName(scope.superteamName);
      this.updateSuperteamSlug(scope.superteamSlug);
    },
    clearScope() {
      this.applyScope({
        userId: null,
        userName: null,
        team: null,
        teamName: null,
        teamSlug: null,
        superteam: null,
        superteamName: null,
        superteamSlug: null,
      });
    },
    // Restore a saved view. Applying the scope re-scopes the whole dashboard;
    // the scope watchers are suppressed (isSwitchingMode) so the leaderboard
    // isn't reloaded from scratch. We then put the saved lists and cursors back
    // and, once the rows have rendered, restore the scroll offset.
    restoreSnapshot(snap) {
      this.tab = snap.tab;
      this.applyScope(snap.scope);
      this.$nextTick(() => {
        this.offsets = { ...snap.offsets };
        this.exhausted = { ...snap.exhausted };
        // Restored alongside the lists it describes, and with the watchers
        // suppressed, so it doesn't reset the resolved children it belongs to.
        this.expandedId = snap.expandedId;
        this.superteamDrilled = snap.superteamDrilled;
        BUCKETS.forEach((key) => {
          this[key] = snap.lists[key] ? snap.lists[key].slice() : [];
        });
        this.$nextTick(() => {
          if (this.$refs.scrollArea)
            this.$refs.scrollArea.scrollTop = snap.scrollTop;
          this.adoptLevel();
          this.isSwitchingMode = false;
          this.updateThumb();
        });
      });
    },
    // Open a mode's global root fresh (no saved view). Clear the scope to global
    // (suppressed watchers, so this doesn't trigger the normal list-wiping
    // reload), reset only that mode's buckets, and load.
    goToModeRootFresh(i) {
      this.clearScope();
      this.$nextTick(() => {
        this.tab = i;
        // A mode's fresh root shows nothing expanded and nothing drilled into,
        // and the buckets an expansion draws on are about to be emptied under it
        // anyway.
        this.expandedId = null;
        this.superteamDrilled = false;
        this.pendingReveal = null;
        this.fillPages = 0;
        this.resetLists(this.bucketsForMode(i));
        this.resetScroll();
        this.adoptLevel();
        this.isSwitchingMode = false;
        this.loadData();
        this.updateThumb();
      });
    },
    // Measure the active option button and park the thumb over it. offsetLeft/
    // offsetWidth are relative to the position:relative track, so they already
    // account for its 3px padding.
    updateThumb() {
      const btn = this.$refs.opts && this.$refs.opts[this.activeMode];
      if (!btn) return;
      this.thumbLeft = btn.offsetLeft;
      this.thumbWidth = btn.offsetWidth;
      // Enable the slide transition only after this first placement.
      this.$nextTick(() => {
        this.thumbReady = true;
      });
    },
    // Mark buckets stale and schedule one reload for this tick. Several watchers
    // can fire for a single composite scope change, so the keys are collected
    // and the fetch happens once.
    queueReset(keys) {
      this.pendingReset = [...new Set([...(this.pendingReset || []), ...keys])];
      if (this.resetQueued) return;
      this.resetQueued = true;
      this.$nextTick(() => {
        const keysToReset = this.pendingReset || [];
        this.pendingReset = null;
        this.resetQueued = false;
        if (this.isSwitchingMode) return;
        this.resetLists(keysToReset);
        // Every in-flight request has just been superseded, and none of them
        // will clear the spinner it owns: fetchPages only does that for a load
        // still answering the current question. Clear both here. The load below
        // sets the one it needs in the same tick, so nothing flickers — and when
        // there ISN'T one (the bucket is still populated, so needsInitialLoad()
        // is false) this is what stops a superseded "load more" leaving
        // loadingMore stuck true, which would gate infinite scroll,
        // revealSuperteam and fillPastGroup at once.
        //
        // Deliberately here and not in resetLists: this is the reset that
        // invalidates the list the spinner belongs to. A reset of a background
        // bucket has no claim on it — see the expandedId watcher.
        if (keysToReset.length) {
          this.loading = false;
          this.loadingMore = false;
        }
        if (this.needsInitialLoad()) this.loadData();
        this.prefetchInactiveTabs();
      });
    },
    // Empty the given buckets and rewind their cursors. Bumping the generation
    // invalidates any in-flight request so a superseded response can't paint
    // over the fresh state — which is why resetting nothing has to do nothing at
    // all: a scope change that leaves every list as it is (a superteam
    // highlighting its row) would otherwise discard a page already on its way
    // and leave the spinner that page owns stuck on.
    //
    // `invalidate: false` empties the buckets without the generation bump, for a
    // reset whose own staleness is already caught by the params check in
    // fetchPages. The bump is panel-wide, so using it to discard one background
    // bucket's page also discards whatever else is in flight — including a "load
    // more" the user is watching, whose spinner then has no owner left to clear
    // it. Clearing that spinner from here would be the wrong repair: it would
    // hide the fact that the page was thrown away for no reason.
    resetLists(keys, { invalidate = true } = {}) {
      if (!keys.length) return;
      if (invalidate) this.generation += 1;
      const offsets = { ...this.offsets };
      const exhausted = { ...this.exhausted };
      const pageFailures = { ...this.pageFailures };
      keys.forEach((key) => {
        this[key] = [];
        offsets[key] = 0;
        exhausted[key] = false;
        // Rewound to offset 0, so the failure count against the old cursor says
        // nothing about the page this bucket will ask for next.
        pageFailures[key] = { offset: null, count: 0 };
      });
      this.offsets = offsets;
      this.exhausted = exhausted;
      this.pageFailures = pageFailures;
    },
    resetScroll() {
      if (this.$refs.scrollArea) this.$refs.scrollArea.scrollTop = 0;
    },
    // Move to the level the scope now points at: file the outgoing level's scroll
    // offset, then put the incoming level's back (top, for one not visited yet).
    // Called from the scope/tab watchers, which all run before the re-render — so
    // the offset read here is still the outgoing list's. A composite change fires
    // several of those watchers in one tick with the scope already fully mutated,
    // so the first call does the work and the rest are no-ops.
    //
    // Restoring on the next tick is enough for the levels whose rows survive the
    // change (drilling back out of a team into the ranking it was picked from). A
    // level whose bucket had to be reset has no rows to scroll yet and lands at
    // the top, which is where a freshly loaded list belongs anyway.
    changeLevel() {
      const to = this.levelKey;
      if (to === this.currentLevelKey) return;
      // Navigating away from a morph in progress: the level restores its own
      // offset, so an anchor from the list being left has no say in it.
      this.pendingAnchor = null;
      // A reveal only ever means the ranking; going anywhere else drops it.
      if (!this.showsRanking) this.pendingReveal = null;
      const el = this.$refs.scrollArea;
      if (el)
        this.scrollByLevel = {
          ...this.scrollByLevel,
          [this.currentLevelKey]: el.scrollTop,
        };
      this.currentLevelKey = to;
      this.$nextTick(() => {
        // A reveal is a scroll target of its own — and a better one than a saved
        // offset, since the level being entered was never scrolled to the row it
        // wants.
        if (this.pendingReveal != null) return;
        if (this.$refs.scrollArea)
          this.$refs.scrollArea.scrollTop = this.scrollByLevel[to] || 0;
      });
    },
    // Layout position of one row inside the scroll area, by its identity key (the
    // same key the transition-group uses). offsetTop, so the FLIP transforms the
    // move animation applies don't affect the reading. null when that row isn't
    // on screen.
    rowTop(key) {
      const el = this.$refs.scrollArea;
      const row = el && el.querySelector(`[data-row-key="${key}"]`);
      return row ? row.offsetTop : null;
    },
    // The same reading for every row on screen, keyed by identity. One pass so the
    // whole snapshot costs a single layout.
    measureRowTops() {
      const el = this.$refs.scrollArea;
      const tops = {};
      if (!el) return tops;
      const rows = el.querySelectorAll("[data-row-key]");
      for (let i = 0; i < rows.length; i++)
        tops[rows[i].dataset.rowKey] = rows[i].offsetTop;
      return tops;
    },
    // A leaving row is taken out of flow so the survivors can start sliding up
    // straight away (see .lb-row-leave-active). With no `top` of its own it falls
    // back to its static position — which, once the rows above it have also left the
    // flow, collapses upward: a run of hidden rows piles onto the same few pixels and
    // fades as one stack of overlapping text. Pinning each row at the offsetTop it
    // held before the morph leaves the whole group fading out where it was.
    pinLeavingRow(el) {
      const top = (this.rowTopsBeforeRender || {})[el.dataset.rowKey];
      if (top != null) el.style.top = `${top}px`;
    },
    // The row is staying after all (a collapse that re-inserts it mid-fade): back to
    // flow position, or the pin would hold it at the old one.
    unpinRow(el) {
      el.style.top = "";
    },
    // Run a scope change that morphs the list in place, keeping one row visually
    // where it is. adoptLevel's intent is "the rows on screen don't budge", which
    // holding scrollTop only achieves while the rows above the anchor are
    // unchanged — untrue when the morph also un-drills a superteam above it.
    //
    // What's held is the row's position in the viewport (offsetTop - scrollTop),
    // reasserted at each step rather than applied once as a delta: the morph
    // settles over several of them, and scrollTop is clamped to the list's height
    // in between, so a one-off correction can be silently swallowed. Recomputing
    // the same absolute goal each time makes the last word the right one.
    //   1. the render itself, on the next tick — registered AFTER the mutation so
    //      the render flush it queues is already ahead of this callback
    //   2. the next frame, once the transition-group has taken the leaving rows
    //      out of flow and the container's height is final
    //   3. when an expanded superteam's missing children land, since the tail
    //      after the group is withheld while they're in flight (see the
    //      resolvingChildren watcher)
    //   4. after each page the silent paging brings in, which is what gives a row
    //      near the end of the list the content below it to be scrolled up over
    //      (the fillingPastGroup watcher)
    anchorRow(key, mutate) {
      const top = this.rowTop(key);
      const el = this.$refs.scrollArea;
      const result = mutate();
      if (top == null || !el) return result;
      this.holdRow(key, top - el.scrollTop);
      return result;
    },
    // Hold one row at a given position in the viewport until the list has settled
    // there. Used with the row's current position to keep it still across a morph
    // (anchorRow), and with 0 to scroll a row up to the top of the list
    // (revealSuperteam).
    holdRow(key, viewportTop) {
      this.pendingAnchor = { key, viewportTop };
      this.$nextTick(() => {
        this.applyAnchor();
        window.requestAnimationFrame(this.settleAnchor);
      });
    },
    // Reassert the goal, and let go once nothing still in flight can move the row.
    settleAnchor() {
      this.applyAnchor();
      if (!this.resolvingChildren && !this.fillingPastGroup)
        this.pendingAnchor = null;
    },
    // Put the anchored row back at the viewport position it was clicked at.
    applyAnchor() {
      const anchor = this.pendingAnchor;
      const el = this.$refs.scrollArea;
      if (!anchor || !el) return;
      const top = this.rowTop(anchor.key);
      if (top != null) el.scrollTop = top - anchor.viewportTop;
    },
    // Take the current scope as the level on screen without moving the scroll:
    // for the changes that keep the rows they had (an in-place superteam expand)
    // or that restore their own offset (a mode switch).
    adoptLevel() {
      this.currentLevelKey = this.levelKey;
    },
    // Whether the list on screen still has to be fetched. A reload triggered by a
    // scope change must not page a bucket it didn't reset: that bucket's cursor is
    // still past its loaded rows, so the fetch would be a "load more" — pulling an
    // unwanted page in and, worse, scrolling to the bottom to show its spinner.
    needsInitialLoad() {
      return !this.displayList.length && !this.exhausted[this.listKey];
    },
    addScrollListener() {
      this.removeScrollListener();
      if (this.$refs.scrollArea)
        this.$refs.scrollArea.addEventListener("scroll", this.handleScroll);
    },
    removeScrollListener() {
      if (this.$refs.scrollArea)
        this.$refs.scrollArea.removeEventListener("scroll", this.handleScroll);
    },
    handleScroll() {
      const el = this.$refs.scrollArea;
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 1) {
        if (
          !this.loading &&
          !this.loadingMore &&
          !this.exhausted[this.listKey]
        ) {
          this.loadData();
        }
      }
    },
    // The buckets this load must fetch. Normally just the current list's next
    // page. The exception is a superteam navigated into: the ranking is fetched
    // underneath its child list, since that's the list the way back up lands on —
    // its first page is already there, and revealSuperteam walks on from it.
    jobsFor(listName, offset) {
      // A list known to have no further pages is skipped: a reload triggered by
      // something else must not request a page past its end and splice a
      // duplicate copy in.
      const jobs = this.exhausted[listName]
        ? []
        : [{ listKey: listName, offset }];
      if (listName === "teams" || this.mode !== "childTeams") return jobs;
      const rankingUnloaded =
        !this.teams.length && !this.exhausted.teams && this.offsets.teams === 0;
      if (rankingUnloaded) jobs.push({ listKey: "teams", offset: 0 });
      return jobs;
    },
    loadData() {
      const listName = this.listKey;
      const offset = this.offsets[listName];
      // A page past the first is a scroll-triggered "load more": show the inline
      // bottom spinner instead of the panel-blocking overlay so the rows already
      // on screen stay visible and scrollable.
      return this.fetchPages(
        this.jobsFor(listName, offset),
        offset > 0 ? "loadingMore" : "loading"
      );
    },
    // Fetch one page for each job. `spinner` picks the blocking overlay, the
    // inline bottom spinner, or neither (a silent background page). A load with
    // several jobs paints once they've all settled.
    async fetchPages(jobs, spinner) {
      const generation = this.generation;
      const isCurrent = () => this.generation === generation;
      const reqs = jobs.map((job) => {
        // Kept alongside the request so the settle hook can tell whether the
        // question this page answers is still the one being asked. See the
        // staleness check below.
        const params = this.paramsFor(job.listKey, job.offset);
        return { ...job, params, req: swrGet(LEADERBOARD_URL, { params }) };
      });
      // Whether the question this page answers is still the one being asked. The
      // generation catches a scope change that emptied a bucket. It can't catch
      // one that didn't: swapping superteams before the first child page lands
      // leaves childTeams empty both times, so the watcher resets nothing
      // (deliberately — see its comment) and no generation bump ever happens.
      // Both pages would then race and the last to land would win, painting one
      // superteam's teams under the other's breadcrumb.
      //
      // Re-deriving the params answers it directly: they carry the scope, so a
      // page requested for a scope the panel has since left no longer matches.
      const stillAsked = (job) =>
        jsonEqual(job.params, this.paramsFor(job.listKey, job.offset));
      reqs.forEach((job) => {
        if (job.req.cached)
          this.applyPage(job.listKey, job.offset, job.req.cached);
      });
      const allCached = reqs.every((job) => job.req.cached);
      if (spinner) {
        // This load supersedes any in-flight one, which can no longer touch
        // spinner state. Clear both so a superseded "load more" can't leave
        // loadingMore stuck true (which would wedge infinite scroll), then set
        // the one this load needs.
        this.loading = false;
        this.loadingMore = false;
        this[spinner] = !allCached;
        // The inline spinner mounts below the rows, so it lands just past the
        // bottom the scroll already sits at. Follow the scroll down to it once it
        // renders so it's actually visible while the page loads.
        if (spinner === "loadingMore" && !allCached) {
          this.$nextTick(() => {
            const el = this.$refs.scrollArea;
            if (el) el.scrollTop = el.scrollHeight;
          });
        }
      }
      try {
        const settled = await Promise.all(
          reqs.map((job) =>
            job.req.fresh
              .then((data) => ({ job, data }))
              .catch((error) => {
                console.log("Error while loading data:", error);
                return { job, data: null };
              })
          )
        );
        if (!isCurrent()) return;
        settled.forEach(({ job, data }) => {
          // Checked per job, so a ranking page riding along in the same load —
          // whose params didn't change — still applies. A page dropped here is
          // not a failed attempt at its offset: nothing was wrong with it except
          // that the panel moved on, and the scope it belongs to has its own
          // cursor.
          if (!stillAsked(job)) return;
          if (!data) return this.notePageFailure(job.listKey, job.offset);
          if (!job.req.cached || !jsonEqual(data, job.req.cached))
            this.applyPage(job.listKey, job.offset, data);
        });
      } finally {
        // The spinner belongs to whichever load is still answering the current
        // question. isCurrent() alone isn't enough to tell: the scope change this
        // guards against is exactly the one that resets nothing and so never
        // bumps the generation, and clearing here would turn off the spinner the
        // load that superseded this one had just switched on.
        if (spinner && isCurrent() && (!reqs.length || reqs.some(stillAsked)))
          this[spinner] = false;
      }
      // Every applied page can bring a scoped superteam's row into the ranking,
      // which is what reveals whether any of its children are missing.
      this.$nextTick(this.settleExpansion);
    },
    // Write one page into a bucket at its offset. splice rather than append, so a
    // revalidated page replaces the cached one it rendered over; only the active
    // list in the response matters (the others ride along empty, and their length
    // says nothing about whether THIS list has more pages). Members/Individuals
    // pages arrive in `data.users`; every team-side page arrives in `data.teams`.
    // A row's own `type` wins, so a click can route by the row rather than by
    // which list it came from; ROW_TYPE_BY_BUCKET names the kind for the lists
    // whose rows don't state one.
    applyPage(listKey, offset, data) {
      const responseKey = listKey === "users" ? "users" : "teams";
      const items =
        data && Array.isArray(data[responseKey]) ? data[responseKey] : null;
      // The key this bucket pages on isn't there at all: the response answered a
      // different question than the one asked (a members page that came back
      // shaped as teams). Treating that as an empty page is what makes it
      // damaging rather than merely useless — empty is indistinguishable from
      // the end of the list, so the bucket gets marked exhausted, paging stops,
      // and snapshotMode/restoreSnapshot carry the flag across a mode toggle so
      // switching away and back doesn't clear it either. Leave the cursor and
      // the flag untouched instead, so this same offset can be retried — bounded
      // by MAX_OFFSET_ATTEMPTS, since nothing else would stop the retry.
      if (!items) return this.notePageFailure(listKey, offset);
      const fallbackType = ROW_TYPE_BY_BUCKET[listKey];
      const rows = items.map((item) => ({
        ...item,
        type: item.type || fallbackType,
      }));
      const list = this[listKey].slice();
      list.splice(offset, this.limit, ...rows);
      this[listKey] = list;
      this.offsets[listKey] = offset + this.limit;
      if (items.length < this.limit) this.exhausted[listKey] = true;
      // The cursor moved, so whatever was failing at this offset isn't any more.
      this.clearPageFailures(listKey);
    },
    // One attempt at this offset came back unusable — an error, or a body without
    // the key this bucket pages on. Both leave the cursor where it was, so the
    // next call asks the same question again; that retry is what recovers from a
    // blip, and what has to be bounded.
    //
    // Giving up marks the bucket exhausted rather than merely dropping the
    // request, because that is the one flag every paging path here terminates on
    // — jobsFor skips an exhausted list, childrenResolved reads it,
    // revealSuperteam and fillPastGroup both stop on it. One write stops all of
    // them, including the chains that drive themselves and would otherwise re-ask
    // for as long as the tab is open.
    notePageFailure(listKey, offset) {
      const prev = this.pageFailures[listKey];
      const count = prev.offset === offset ? prev.count + 1 : 1;
      this.pageFailures = {
        ...this.pageFailures,
        [listKey]: { offset, count },
      };
      if (count >= MAX_OFFSET_ATTEMPTS)
        this.exhausted = { ...this.exhausted, [listKey]: true };
    },
    clearPageFailures(listKey) {
      if (this.pageFailures[listKey].offset === null) return;
      this.pageFailures = {
        ...this.pageFailures,
        [listKey]: { offset: null, count: 0 },
      };
    },
    // Request params for one bucket's page. Kept in one place so the prefetch's
    // SWR cache key is guaranteed to match what a later load will request.
    paramsFor(listKey, offset) {
      // The expanded superteam's member list, which carries each child's rank in
      // the merged ranking — that rank is what lets a child fetched here be placed
      // in the list on screen.
      //
      // Keyed on the one superteam id, so every page of the chain asks the same
      // question. The by-id alternative (`team_ids=`) had to freeze its id list
      // first, because the set of children missing from the loaded pages shrinks as
      // the ranking loads more of them, and an offset means nothing against a list
      // that changes between requests.
      //
      // Note this is the same request `childTeams` makes when a superteam is
      // navigated INTO — deliberately, so the two share an SWR cache entry. They
      // stay separate buckets because they can hold different superteams: one is
      // keyed on the scope, this one on whatever the chevron opened.
      if (listKey === "resolvedChildren") {
        return this.withSharedParams({
          country: this.country,
          offset,
          limit: this.limit,
          superteam: this.expandedId,
        });
      }
      if (listKey === "childTeams") {
        return this.withSharedParams({
          country: this.country,
          offset,
          limit: this.limit,
          superteam: this.superteam,
        });
      }
      if (listKey === "users" && this.mode === "members") {
        return this.withSharedParams({
          country: this.country,
          offset,
          limit: this.limit,
          team: this.team,
        });
      }
      return this.buildParams(listKey, offset);
    },
    buildParams(listKey, offset) {
      return this.withSharedParams({
        country: this.country,
        // No user_id: a selected individual narrows the map and stats, not the
        // ranking they were picked from. TeamFilter.suggestionParams mirrors this
        // param set for SWR cache-key parity — the two have to drop it together.
        offset,
        limit: this.limit,
        tabItems: TAB_ITEMS_BY_BUCKET[listKey],
      });
    },
    // The params every list shares, whichever bucket is being paged: the date
    // window and the sort column. Every builder above funnels through here, so a
    // list can't be fetched in one order while another is fetched in another.
    // TeamFilter's suggestions and MapView's country prefetch send the default
    // sort to keep their SWR cache keys matching a freshly loaded panel's.
    withSharedParams(params) {
      if (this.startDay && this.endDay) {
        params.start_date = this.startDay;
        params.end_date = this.endDay;
      }
      params.sortBy = this.sortBy;
      return params;
    },
    // Everything that has to happen after a page lands, in order: bring a
    // superteam being looked for into view, find the expanded group's children
    // that the loaded pages don't cover, then page forward to where the group
    // ends. The reveal comes first: until its row is loaded there is no expansion
    // for the other two to settle.
    settleExpansion() {
      this.revealSuperteam();
      this.resolveMissingChildren();
      this.fillPastGroup();
    },
    // Ask for a superteam's row to be brought into view in the ranking. Cleared
    // as soon as it is, when the ranking runs out, when the walk gives up, or when
    // the user navigates somewhere the reveal doesn't apply to (changeLevel).
    queueReveal(id) {
      this.pendingReveal = id;
      this.revealPages = 0;
    },
    // Scroll the ranking to a superteam's row, paging silently until that row is
    // loaded. It's what the way back up out of a superteam lands on, and since the
    // row is also what the expansion morphs around, finding it opens the group.
    //
    // The walk is the only way to get there: the ranking is offset-paged, so a row
    // 300 places down needs every row above it loaded to be scrolled to. Ranks
    // only increase, so it terminates on the row, on an exhausted ranking, or at
    // REVEAL_MAX_PAGES — past which a jump would be into a part of the list no
    // amount of scrolling reaches by hand anyway, and the child list the user came
    // from was the better view of it regardless.
    revealSuperteam() {
      const id = this.pendingReveal;
      if (id == null || !this.showsRanking) return;
      if (this.teams.some((row) => row.type === "superteam" && row.id === id)) {
        this.pendingReveal = null;
        // Held rather than set once: the group opening under it withholds and then
        // restores the rows below, and each of those changes the height the scroll
        // is clamped to.
        return this.holdRow(`superteam:${id}`, 0);
      }
      if (this.exhausted.teams || this.revealPages >= REVEAL_MAX_PAGES) {
        this.pendingReveal = null;
        return;
      }
      if (this.revealing || this.loading || this.loadingMore) return;
      // A reset queued for this tick bumps the generation, which would discard the
      // page this walk asks for. Leave it to the settle hook that runs after it.
      if (this.resetQueued) return;
      this.revealing = true;
      this.revealPages += 1;
      return this.fetchPages(this.jobsFor("teams", this.offsets.teams), null)
        .catch(() => {})
        .then(() => {
          this.revealing = false;
          // Chain from here rather than from fetchPages' settle hook, which may
          // have run before this one — the same reason fillPastGroup does.
          this.$nextTick(this.revealSuperteam);
        });
    },
    // A group whose last child ranks past the loaded pages leaves the list ending
    // at the group: every loaded row after the superteam ranks above that child, so
    // there's no tail row yet — and no bottom holding one to scroll to. Page the
    // ranking forward (silently — no spinner, and so no scroll-to-the-spinner
    // either) until it reaches past the group; from there the tail is on screen and
    // ordinary infinite scroll takes over.
    //
    // Bounded three ways: each page's ranks are higher than the last's, so the
    // chain stops as soon as one clears `groupEndRank`; it stops when the ranking
    // runs out; and it stops at FILL_MAX_PAGES, since `groupEndRank` is a rank the
    // server chose and can sit arbitrarily deep. Giving up only costs the tail
    // below the group — never a child team, which the by-id lookup supplies.
    fillPastGroup() {
      if (!this.isSuperteamExpanded || this.fillingPastGroup) return;
      if (this.loading || this.loadingMore) return;
      // Infinity while the children are being looked up: where the group ends is
      // still unknown, so there's nothing to page towards yet.
      if (this.exhausted.teams || this.groupEndRank === Infinity) return;
      if (this.fillPages >= FILL_MAX_PAGES) return;
      const lastIndex = this.teams.length - 1;
      const lastRank =
        lastIndex < 0 ? 0 : this.rankAt(this.teams[lastIndex], lastIndex);
      if (lastRank > this.groupEndRank) return;
      this.fillPages += 1;
      this.fillingPastGroup = true;
      const jobs = this.jobsFor("teams", this.offsets.teams);
      return this.fetchPages(jobs, null)
        .catch(() => {})
        .then(() => {
          this.fillingPastGroup = false;
          // Continue from here rather than leaning on fetchPages' own settle hook,
          // so the next page is asked for even if that hook runs before this one.
          // A collapse in the meantime makes the next call a no-op.
          this.$nextTick(this.fillPastGroup);
        });
    },
    // Background requests (no spinner) fetching the expanded superteam's member
    // list, which is where a child that has no row in the loaded pages gets both
    // its name and its place in the ranking.
    //
    // Only fired when there IS such a child: with every child already on screen the
    // list holds nothing new, and an expand should cost no request at all.
    //
    // Paged to the end rather than fetched once — a superteam can have more members
    // than a page holds (a school cohort of 25 classes is one request short of
    // complete), and stopping early would also compute `groupEndRank` from a short
    // set and cut the tail in the wrong place. A child the server gives no rank has
    // no position in the ranking and shows a dash.
    resolveMissingChildren() {
      const pending =
        this.isSuperteamExpanded &&
        this.missingChildCount > 0 &&
        !this.loading &&
        !this.loadingMore &&
        !this.childrenResolved;
      // Withhold the rows after the group until the children are placed — see
      // expandedRows.
      this.resolvingChildren = pending;
      if (!pending || this.resolvingChildrenPage) return;
      const offset = this.offsets.resolvedChildren;
      this.resolvingChildrenPage = true;
      return this.fetchPages([{ listKey: "resolvedChildren", offset }], null)
        .catch(() => {})
        .then(() => {
          this.resolvingChildrenPage = false;
          // Chain from here rather than leaning on fetchPages' own settle hook, so
          // the next page is asked for even if that hook runs before this one — the
          // same reason fillPastGroup does. A collapse in the meantime makes the
          // next call a no-op.
          this.$nextTick(this.resolveMissingChildren);
        });
    },
    // Warm the SWR cache for the mode the user is NOT on (first page only), so
    // switching Individuals <-> Teams renders instantly instead of spinning.
    // Only the global (unscoped) view has that choice; a team- or
    // superteam-scoped view is a single list with nothing to prefetch.
    prefetchInactiveTabs() {
      if (this.mode !== "global") return;
      this.tabItems.forEach((_, i) => {
        if (i === this.activeMode) return;
        this.bucketsForMode(i).forEach((key) => {
          if (!PREFETCHABLE_BUCKETS.includes(key)) return;
          // Skip a bucket already populated (or known empty) for these filters.
          if (this.offsets[key] > 0 || this.exhausted[key]) return;
          // Speculative warm-up for a mode the user has not switched to yet;
          // `silent` keeps its failures out of Sentry (see utils/reportError).
          swrGet(LEADERBOARD_URL, {
            params: this.buildParams(key, 0),
            silent: true,
          }).fresh.catch(() => {});
        });
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/styles/variables.scss";

// One knob for the row dissolve/move timing (expanding a superteam), kept in the
// same family as the toggle thumb's slide so the panel's motion reads as one.
$lb-row-anim: 0.3s;
$lb-row-ease: cubic-bezier(0.4, 0, 0.2, 1);

// Vertical gap between rows, measured content-to-content. Named because the
// child rows' thread line has to bridge it to read as one continuous branch.
$lb-row-gap: 24px;

// Vertical padding inside a row, so the selected row's highlighted surface
// clears its text instead of hugging it. Subtracted from the row margin below
// so the content-to-content gap stays $lb-row-gap.
$lb-row-pad-y: 6px;

// Gap between the fixed header and the first row, also content-to-content.
$lb-first-row-gap: 15px;

// Panel height. This fixed value is what the panel uses whenever its container
// doesn't size it — the dashboard's single-column layout, and any other host.
// A host that does want to size it sets `--lb-height` on any ancestor: the
// dashboard's two-column layout sets it to 100% so the panel fills the space
// its column has left over, and its bottom edge lines up with the other
// column's. Whatever the height ends up being, only `.lb-rows` scrolls.
$lb-fixed-height: 530px;

.lb-wrap {
  position: relative;
  display: flex;
  flex-direction: column;
  height: var(--lb-height, #{$lb-fixed-height});
}

// Fixed header (title/tabs + column headings). Sits above the scrolling
// .lb-rows; the shadow makes the scroll-under effect obvious.
.lb-header {
  flex: none;
  z-index: 3;
  background: $color-panel;
  padding-bottom: 8px;
  box-shadow: 0 6px 8px -4px rgba(0, 0, 0, 0.18);
  border-radius: 16px 16px 0 0;
}

.theme--dark .lb-header {
  box-shadow: 0 6px 8px -4px rgba(0, 0, 0, 0.6);
}

.lb-bar {
  padding: 16px;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.lb-title {
  font-size: 20px;
  font-weight: 700;
}

// Breadcrumb: root tab > (superteam >) drilled team. Link segments are subtle
// links so they read as "click to go up a level".
.lb-breadcrumb {
  display: inline-flex;
  align-items: center;

  .lb-crumb-sep {
    margin: 0 2px;
    color: $color-text-muted;
  }
  .lb-crumb-link {
    cursor: pointer;
    color: $color-text-primary;

    &:hover {
      text-decoration: underline;
    }
  }
}

// Prominent segmented switch for Individuals / Teams. A pill track with the
// active option filled by the brand gradient (matching the active-chip look).
.lb-toggle {
  position: relative;
  // Options take only as much width as their label needs; the thumb is measured
  // to match each one (see updateThumb).
  display: inline-flex;
  padding: 1px;
  background: $color-raised;
  border-radius: 999px;
}

.lb-toggle__thumb {
  position: absolute;
  top: -2px;
  bottom: -1px;
  // left/width are set inline from the measured active option.
  border-radius: 999px;
  background: $color-primary-gradient;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
  transition: left 0.25s cubic-bezier(0.4, 0, 0.2, 1),
    width 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}

// The thumb is lime in both modes, so its own shadow stays; no override needed.

.lb-toggle__opt {
  position: relative; // sit above the sliding thumb
  z-index: 1;
  padding: 6px 10px;
  border: none;
  border-radius: 999px;
  background: transparent;
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
  color: $color-text-muted;
  cursor: pointer;
  transition: color 0.2s ease;

  // Thin divider between adjacent options. Hidden on the active option and on the
  // option right after it, since the sliding thumb already separates those edges.
  // :first-of-type (not :first-child) skips the leftmost button — the thumb <span>
  // is the track's actual first child, so :first-child would match no button.
  &:not(:first-of-type)::before {
    content: "";
    position: absolute;
    left: 0;
    top: 6px;
    bottom: 6px;
    width: 1px;
    background: $color-divider;
    transition: opacity 0.2s ease;
  }

  &.lb-toggle__opt--active::before,
  &.lb-toggle__opt--active + .lb-toggle__opt::before {
    opacity: 0;
  }

  &.lb-toggle__opt--active {
    color: $color-on-primary;
  }

  &:hover:not(.lb-toggle__opt--active) {
    color: $color-text-primary;
  }
}

.th {
  color: #1d211f;
  font-weight: 800;
  padding: 0 20px;
  // Keep every column on one line. The rank column's min-width can push the
  // combined widths past 100%; without this the row (v-row defaults to
  // flex-wrap: wrap) would drop the last column to a new line. Columns shrink
  // and clip via their ellipsis instead.
  flex-wrap: nowrap;

  // Headers get the same nowrap/ellipsis as the data rows below, so a squeezed
  // column clips rather than wraps to a second line.
  .col {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

.theme--dark .th {
  color: var(--v-textPrimary-base);
}

// A sortable column heading. Reads as the heading it replaced — same size and
// weight, inheriting the header's colour — with the arrow as the only added
// affordance.
.lb-sort {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  max-width: 100%;
  padding: 0;
  border: none;
  background: none;
  font: inherit;
  color: inherit;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }

  &.lb-sort--active {
    font-weight: 700;
  }
}

// The arrow is always in the layout, so a column becoming the sorted one doesn't
// shift its heading; only its opacity changes. It takes the header's own text
// colour in every state — the lime brand colour is for surfaces (the toggle thumb,
// active chips), and a 14px glyph of it on the panel reads as absent.
.lb-sort-icon {
  flex: none;
  opacity: 0;
  color: inherit !important;
  transition: opacity 0.15s ease;

  .lb-sort:hover &,
  .lb-sort--active & {
    opacity: 1;
  }
}

// Rows container. position: relative so a row leaving the list can be taken out
// of flow (see .lb-row-leave-active) while the surviving rows animate into their
// new places.
.lb-row-group {
  position: relative;
}

.tr {
  padding: $lb-row-pad-y 20px;
  // The row's own padding contributes to the gap, so the margin carries only the
  // remainder — the content-to-content spacing is unchanged by the padding.
  margin-top: $lb-row-gap - 2 * $lb-row-pad-y;
  flex-wrap: nowrap; // single-line rows (see .th note above)
  align-items: center; // vertically centre every column on the row
  // The whole row is the click target, so the whole row has to look like one:
  // the surface it lifts to on hover is the same one a selected row sits on, and
  // the selected row keeps its brand bar to stay distinguishable under a hover.
  cursor: pointer;
  border-radius: 6px;
  transition: background-color 0.15s ease;

  &:hover {
    background: $color-raised;
  }

  &:hover .lb-row-name {
    text-decoration: underline;
  }

  // The row is a button by role, so keyboard focus has to be as visible as the
  // hover it mirrors. Outline rather than a background swap, so a focused row
  // that is also selected still shows its brand bar underneath.
  &:focus-visible {
    background: $color-raised;
    outline: 2px solid var(--v-primary-base);
    outline-offset: -2px;
  }

  &:focus-visible .lb-row-name {
    text-decoration: underline;
  }

  .col {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  // Same idea against the header: $lb-first-row-gap of clear space below it.
  &:first-child {
    margin-top: $lb-first-row-gap - $lb-row-pad-y;
  }

  .col:first-child {
    font-weight: bold;
  }
}

// The selected strider's row. Picking someone hides nobody, so the row itself is
// what says who the dashboard is scoped to: the row's own surface lifts to the
// raised level and a brand bar runs down its leading edge. The bar sits in the
// row's horizontal padding, so nothing on the row shifts when it appears.
.tr--selected {
  position: relative;
  background: $color-raised;
  border-radius: 6px;

  &::before {
    content: "";
    position: absolute;
    left: 6px;
    // Inset from the surface's edges by the same amount at every side, so the
    // bar grows with the row padding instead of staying pinned to the text.
    top: $lb-row-pad-y - 2px;
    bottom: $lb-row-pad-y - 2px;
    width: 3px;
    border-radius: 3px;
    background: var(--v-primary-base);
  }
}

// A child team listed under its expanded superteam: indented past its rank, with
// a vertical thread line running down the indent. The ranks stay non-contiguous,
// so the line is what makes the group read as one branch of its superteam.
$lb-child-indent: 30px;
$lb-child-line-x: 12px; // where the line sits inside the indent

.tr--child .lb-name-col {
  position: relative;
  padding-left: $lb-child-indent;
  // The line runs up into the gap above the row to meet the previous row, so
  // consecutive children (and the superteam itself) are joined by one stroke.
  overflow: visible;

  &::before {
    content: "";
    position: absolute;
    left: $lb-child-line-x;
    top: -$lb-row-gap;
    bottom: 0;
    border-left: 1px solid $color-divider;
  }
}

// Row dissolve/move: rows hidden by an expand (or revealed by a collapse) fade,
// and every surviving row slides to its new position.
.lb-row-move {
  transition: transform $lb-row-anim $lb-row-ease;
}

.lb-row-enter-active {
  transition: opacity $lb-row-anim $lb-row-ease;
}

// A leaving row overlaps whichever survivor slides up underneath it, so it clears
// out faster than the move it's uncovering: by the time a row has travelled far
// enough for the overlap to read as two rows at once, the one on top is gone.
$lb-row-fade-out: 0.16s;
.lb-row-leave-active {
  transition: opacity $lb-row-fade-out ease-out;
}

.lb-row-enter,
.lb-row-leave-to {
  opacity: 0;
}

// Leaving rows are pulled out of flow so the rows that stay can animate up into
// the gap instead of waiting for the fade to finish. `top` is set per row as it
// leaves (pinLeavingRow) — without it they fall back to a static position that has
// already collapsed upward, stacking the whole run of hidden rows in one place.
.lb-row-leave-active {
  position: absolute;
  left: 0;
  right: 0;
  pointer-events: none; // it's on its way out; clicks belong to the rows beneath
}

// The rank column is only 1/12 of the row, so on a narrow panel its proportional
// slice collapses below the "No." label (and multi-digit ranks). A min-width
// floor — which overrides Vuetify's max-width cap — keeps it wide enough to fit,
// so the header no longer wraps. Applied to header + rows to stay aligned.
$lb-rank-min-width: 34px;
.th .col:first-child,
.tr .col:first-child {
  min-width: $lb-rank-min-width;
  // Let an enlarged medal spill beyond the row's text line box without clipping.
  overflow: visible;
}

// Rank medal (🥇🥈🥉). font-size is the tunable knob; line-height: 0 keeps the
// enlarged emoji from growing the row's line box (which would make the row taller
// than the text, push the top-3 rows apart, and top-align the other columns).
// It overflows its zero-height box symmetrically and is centred by the row's
// align-items: center.
.lb-medal {
  display: inline-block;
  vertical-align: middle;
  font-size: 28px;
  line-height: 0;
  margin-left: -3px;
  margin-top: -7px;
}

.lb-number {
  padding-left: 5px;
}

// The name cell's contents: a flex row so the name is the only part that gives
// way when the panel is narrow. The click affordance lives on the row around it
// (see .tr) — this is just the layout.
.lb-row-link {
  display: inline-flex;
  align-items: center;
  max-width: 100%;
  color: $color-text-primary;
}

// Name over affiliation subtitle: the one part of the row that shrinks, so the
// icons on either side of it stay whole. min-width: 0 overrides the flex default
// that refuses to shrink an item below its text; both lines then truncate
// themselves rather than letting the cell clip the row, which would take the
// trailing chevron with it.
.lb-row-label {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.lb-row-name,
.lb-row-subtitle {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

// What the row belongs to (see rowSubtitle) — secondary by weight and colour, so
// it reads as an attribute of the name above it rather than a second name. Only
// rows that have one are taller; reserving the space on every row would put an
// empty gap under most names.
.lb-row-subtitle {
  font-size: 11px;
  line-height: 1.3;
  color: $color-text-muted;
}

// Superteam row markers: the $graph2 glyph in front of the name, and an expander
// chevron that flips when its child teams are showing. Neither shrinks.
.lb-row-icon {
  flex: none;
  margin-right: 6px;
  color: $color-text-muted;
}

// The expander is its own control inside a clickable row (the row scopes, this
// shows the children), so it gets a hit area of its own — padding around the
// glyph, and a hover tint marking it as the thing under the cursor rather than
// the row.
.lb-row-expander {
  flex: none;
  display: inline-flex;
  padding: 2px;
  margin-left: 2px;
  border: none;
  border-radius: 50%;
  background: transparent;
  cursor: pointer;

  &:hover .lb-row-chevron,
  &:focus-visible .lb-row-chevron {
    color: $color-text-primary;
  }
}

.lb-row-chevron {
  flex: none;
  color: $color-text-muted;
  transition: transform $lb-row-anim $lb-row-ease;

  &.lb-row-chevron--open {
    transform: rotate(180deg);
  }
}

// Only the rows scroll; the header above stays fixed.
.lb-rows {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  overflow-y: scroll;
  padding-bottom: 20px;
}

// Inline "loading next page" spinner, centred below the current rows.
.lb-load-more {
  display: flex;
  justify-content: center;
  padding: 16px 0 4px;
}

.empty {
  flex: 1 1 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;

  span {
    font-size: 16px;
    margin-top: 10px;
    color: $color-text-muted;
  }
}
</style>
